'use client';

import { useState, useMemo } from 'react';
import { userSchema } from '@numdle/shared';

export default function UserForm({ onSubmit, initialData = {}, sessionData, sessionId }) {
  if (!sessionData || !sessionId) {
    return null;
  }
  const schemaShape = userSchema._def.shape();
  
  const defaultValues = useMemo(() => {
    const defaults = {
      phone: sessionData.phone,
      name: sessionData.name || initialData.name || '',
      email: sessionData.email || initialData.email || ''
    };
    const shape = userSchema._def.shape();
    Object.keys(shape).forEach(key => {
      if (defaults[key] !== undefined) return;
      
      const fieldDef = shape[key];
      let innerType = fieldDef;
      if (fieldDef._def.typeName === 'ZodOptional') {
        innerType = fieldDef._def.innerType;
      }
      if (innerType._def.typeName === 'ZodArray') {
        defaults[key] = initialData[key] || [];
      } else {
        defaults[key] = initialData[key] || '';
      }
    });
    return defaults;
  }, [sessionData]);

  const [formData, setFormData] = useState(defaultValues);
  const [errors, setErrors] = useState({});
  const [arrayInputs, setArrayInputs] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const addArrayItem = (fieldName) => {
    const inputValue = arrayInputs[fieldName] || '';
    const fieldDef = schemaShape[fieldName];
    let innerType = fieldDef;
    if (fieldDef._def.typeName === 'ZodOptional') {
      innerType = fieldDef._def.innerType;
    }
    const maxLength = innerType._def.maxLength?.value || Infinity;
    
    if (inputValue.trim() && formData[fieldName].length < maxLength) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: [...prev[fieldName], inputValue.trim()]
      }));
      setArrayInputs(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const removeArrayItem = (fieldName, index) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const result = userSchema.safeParse(formData);
    
    if (!result.success) {
      const fieldErrors = {};
      result.error.errors.forEach(err => {
        const field = err.path[0];
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }
    
    setErrors({});
    onSubmit(result.data);
  };

  const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  };

  const errorStyle = {
    color: '#dc2626',
    fontSize: '12px',
    marginTop: '0.25rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.25rem',
    fontWeight: '500',
    fontSize: '14px'
  };

  const fieldStyle = {
    marginBottom: '1rem'
  };

  const getFieldConfig = (fieldName) => {
    const fieldDef = schemaShape[fieldName];
    let innerType = fieldDef;
    
    if (fieldDef._def.typeName === 'ZodOptional') {
      innerType = fieldDef._def.innerType;
    }
    
    const typeName = innerType._def.typeName;
    
    let inputType = 'text';
    let isTextarea = false;
    let isArray = false;
    let maxLength = null;
    
    if (typeName === 'ZodString') {
      const checks = innerType._def.checks || [];
      const hasUrl = checks.some(c => c.kind === 'url');
      const hasEmail = checks.some(c => c.kind === 'email');
      const maxCheck = checks.find(c => c.kind === 'max');
      
      if (maxCheck) maxLength = maxCheck.value;
      if (hasEmail) inputType = 'email';
      else if (hasUrl) inputType = 'url';
      
      if (maxLength && maxLength >= 200) isTextarea = true;
    } else if (typeName === 'ZodArray') {
      isArray = true;
    }
    
    const label = fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
    
    return {
      label,
      type: isArray ? 'array' : isTextarea ? 'textarea' : inputType,
      maxLength
    };
  };

  const isRequired = (fieldName) => {
    const fieldDef = schemaShape[fieldName];
    return !fieldDef.isOptional();
  };

  const renderField = (fieldName) => {
    const config = getFieldConfig(fieldName);
    const required = isRequired(fieldName);

    if (config.type === 'array') {
      const fieldDef = schemaShape[fieldName];
      let innerType = fieldDef;
      if (fieldDef._def.typeName === 'ZodOptional') {
        innerType = fieldDef._def.innerType;
      }
      const maxLength = innerType._def.maxLength?.value || 10;
      
      return (
        <div key={fieldName} style={fieldStyle}>
          <label style={labelStyle}>{config.label}</label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input
              type="text"
              value={arrayInputs[fieldName] || ''}
              onChange={(e) => setArrayInputs(prev => ({ ...prev, [fieldName]: e.target.value }))}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addArrayItem(fieldName))}
              style={{ ...inputStyle, flex: 1 }}
              placeholder={`Add ${fieldName}`}
            />
            <button
              type="button"
              onClick={() => addArrayItem(fieldName)}
              disabled={formData[fieldName].length >= maxLength}
              style={{
                padding: '0.5rem 1rem',
                background: formData[fieldName].length >= maxLength ? '#ccc' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: formData[fieldName].length >= maxLength ? 'not-allowed' : 'pointer'
              }}
            >
              Add
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {formData[fieldName].map((item, index) => (
              <span
                key={index}
                style={{
                  padding: '0.25rem 0.75rem',
                  background: '#e5e7eb',
                  borderRadius: '16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {item}
                <button
                  type="button"
                  onClick={() => removeArrayItem(fieldName, index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '16px',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          {errors[fieldName] && <div style={errorStyle}>{errors[fieldName]}</div>}
        </div>
      );
    }

    if (config.type === 'textarea') {
      return (
        <div key={fieldName} style={fieldStyle}>
          <label style={labelStyle}>
            {config.label} {required && '*'}
          </label>
          <textarea
            name={fieldName}
            value={formData[fieldName]}
            onChange={handleChange}
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            maxLength={config.maxLength}
          />
          {errors[fieldName] && <div style={errorStyle}>{errors[fieldName]}</div>}
        </div>
      );
    }

    const isPhoneField = fieldName === 'phone';
    
    return (
      <div key={fieldName} style={fieldStyle}>
        <label style={labelStyle}>
          {config.label} {required && '*'}
          {isPhoneField && (
            <span style={{ marginLeft: '0.5rem', fontSize: '12px', color: '#10b981', fontWeight: 'normal' }}>
              ✓ Verified by Truecaller
            </span>
          )}
        </label>
        <input
          type={config.type}
          name={fieldName}
          value={formData[fieldName]}
          onChange={handleChange}
          style={{
            ...inputStyle,
            ...(isPhoneField && {
              background: '#f3f4f6',
              cursor: 'not-allowed',
              color: '#6b7280'
            })
          }}
          disabled={isPhoneField}
          readOnly={isPhoneField}
        />
        {errors[fieldName] && <div style={errorStyle}>{errors[fieldName]}</div>}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: '600px', margin: '0 auto' }}>
      {Object.keys(schemaShape).map(fieldName => renderField(fieldName))}

      <button
        type="submit"
        style={{
          width: '100%',
          padding: '0.75rem',
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          fontSize: '16px',
          fontWeight: '500',
          cursor: 'pointer',
          marginTop: '1rem'
        }}
      >
        Submit
      </button>
    </form>
  );
}
